import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';

const chartData = [
  { x: 0, live: 0, expected: 0 },
  { x: 1, live: 1.14, expected: 2.1 },
  { x: 2, live: 4.97, expected: 4.8 },
  { x: 3, live: 3.67, expected: 6.2 },
  { x: 4, live: 8.32, expected: 8.9 },
  { x: 5, live: 7.18, expected: 10.5 },
  { x: 6, live: 11.6, expected: 13.2 },
  { x: 7, live: 14.3, expected: 15.4 },
  { x: 8, live: 12.8, expected: 17.1 },
  { x: 9, live: 16.9, expected: 19.8 },
  { x: 10, live: 21.4, expected: 22.0 },
  { x: 11, live: 19.7, expected: 24.3 },
  { x: 12, live: 24.2, expected: 26.8 },
];

const trades = [
  { date: 'Mar 1', side: 'Long', entry: '$67,420', exit: '$68,190', ret: '+1.14%', win: true },
  { date: 'Feb 26', side: 'Long', entry: '$64,850', exit: '$67,310', ret: '+3.79%', win: true },
  { date: 'Feb 22', side: 'Long', entry: '$66,100', exit: '$65,280', ret: '-1.24%', win: false },
  { date: 'Feb 18', side: 'Long', entry: '$61,940', exit: '$64,720', ret: '+4.49%', win: true },
  { date: 'Feb 14', side: 'Long', entry: '$63,200', exit: '$62,510', ret: '-1.09%', win: false },
];

const AdaptiveFlowPreviewMock = () => (
  <div className="rounded-lg overflow-hidden shadow-2xl shadow-black/30 border border-gray-500/25 ring-1 ring-white/[0.04] font-mono" style={{ background: '#0f1923' }}>
    {/* Two-column: Trades + Parameters | Chart + Risk */}
    <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-px" style={{ background: 'rgba(255,255,255,0.02)' }}>
      {/* Left column */}
      <div style={{ background: '#0f1923' }}>
        {/* Live Validation Progress */}
        <div className="px-4 py-3 border-b border-white/[0.04]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] text-white uppercase tracking-wider font-semibold">Live Validation Progress</span>
            <span className="text-[9px] text-gray-500">Est. re-optimization: <span className="text-white">~18.6 months</span></span>
          </div>
          <div className="flex items-center justify-between text-[9px] text-gray-500 mb-1">
            <span>Candles to Re-optimization</span>
            <span className="text-gray-400">247 / 720</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div className="h-full rounded-full bg-emerald-400/70" style={{ width: '34%' }} />
          </div>
          <div className="flex items-center justify-between mt-2 text-[9px]">
            <span className="text-gray-500">Trades: <span className="text-white font-medium">12</span> <span className="text-gray-600">(min: 5)</span></span>
            <span className="text-gray-500">Progress: <span className="text-emerald-400">34%</span></span>
          </div>
        </div>

        {/* Active Strategy Parameters */}
        <div className="px-4 py-3 border-b border-white/[0.04]">
          <span className="text-[10px] text-white uppercase tracking-wider font-semibold">Active Strategy Parameters</span>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-[11px] bg-white/[0.06] text-gray-300 px-2.5 py-1 rounded">fastLength: <span className="text-white font-semibold">34</span></span>
            <span className="text-[11px] bg-white/[0.06] text-gray-300 px-2.5 py-1 rounded">slowLength: <span className="text-white font-semibold">40</span></span>
          </div>
          <p className="text-[9px] text-gray-600 mt-2">Last optimized: Jan 8, 2026</p>
        </div>

        {/* Recent Trades */}
        <div className="px-4 py-3">
          <span className="text-[10px] text-white uppercase tracking-wider font-semibold">Recent Trades</span>
          <table className="w-full text-[9px] mt-2">
            <thead>
              <tr className="text-gray-500 uppercase tracking-wider">
                <th className="text-left font-normal pb-1.5">Date</th>
                <th className="text-left font-normal pb-1.5">Side</th>
                <th className="text-right font-normal pb-1.5">Entry</th>
                <th className="text-right font-normal pb-1.5">Exit</th>
                <th className="text-right font-normal pb-1.5">Return</th>
              </tr>
            </thead>
            <tbody className="text-gray-400">
              {trades.map((t, i) => (
                <tr key={i} className="border-t border-white/[0.03]">
                  <td className="py-1.5 text-gray-500">{t.date}</td>
                  <td className="py-1.5 text-cyan-400/80">{t.side}</td>
                  <td className="py-1.5 text-right text-gray-300">{t.entry}</td>
                  <td className="py-1.5 text-right text-gray-300">{t.exit}</td>
                  <td className={`py-1.5 text-right font-medium ${t.win ? 'text-emerald-400' : 'text-red-400'}`}>{t.ret}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Right column */}
      <div className="flex flex-col" style={{ background: '#0f1923' }}>
        {/* Cumulative Return Comparison */}
        <div className="px-4 py-3 border-b border-white/[0.04] flex-1 flex flex-col min-h-[260px]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] text-white uppercase tracking-wider font-semibold">Cumulative Return Comparison</span>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <span className="w-2 h-0.5 bg-cyan-400 rounded" />
                <span className="text-[8px] text-gray-500">Live</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-0.5 bg-gray-500 rounded" />
                <span className="text-[8px] text-gray-500">Expected</span>
              </div>
            </div>
          </div>
          <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: -15 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="x" tick={{ fontSize: 8, fill: '#4b5563' }} axisLine={{ stroke: 'rgba(255,255,255,0.06)' }} tickLine={false} />
                <YAxis tick={{ fontSize: 8, fill: '#4b5563' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${v}%`} />
                <Tooltip
                  contentStyle={{ background: '#1a2332', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 4, fontSize: 10 }}
                  labelStyle={{ color: '#9ca3af' }}
                  formatter={(value: number) => [`${value.toFixed(1)}%`]}
                />
                <Area type="monotone" dataKey="expected" stroke="rgba(107,114,128,0.5)" fill="rgba(107,114,128,0.05)" strokeDasharray="4 4" strokeWidth={1.5} dot={false} />
                <Area type="monotone" dataKey="live" stroke="#22d3ee" fill="rgba(34,211,238,0.08)" strokeWidth={2} dot={{ r: 3, fill: '#22d3ee', stroke: '#0f1923', strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Risk Tolerance */}
        <div className="px-4 py-3">
          <span className="text-[10px] text-white uppercase tracking-wider font-semibold">Risk Tolerance</span>
          <div className="flex items-center justify-between mt-2 mb-1">
            <span className="text-[9px] text-cyan-400">Risk Level</span>
            <span className="text-[9px] text-emerald-400">Low-Moderate</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400" style={{ width: '30%' }} />
          </div>
          <div className="flex justify-between mt-1 text-[8px] text-gray-600">
            <span>Low</span>
            <span>Moderate</span>
            <span>High</span>
            <span>Extreme</span>
          </div>
          <div className="flex items-center justify-between mt-3 text-[9px]">
            <span className="text-gray-500">Drawdown recovery: <span className="text-white">~8 months</span></span>
            <span className="text-gray-500">Re-opt: <span className="text-emerald-400">Every 1.7 year</span></span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default AdaptiveFlowPreviewMock;
